import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import type { LoomVideo } from '@loomhq/record-sdk';

import VideoIcon from '@atlaskit/icon/core/video';
import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { LoomIcon } from '@atlaskit/logo';

import { useLoomInsertUrlActions } from '@confluence/loom-insert-url-store';
import { type FlagsStateContainer } from '@confluence/flags';
import { getUrlForContentType } from '@confluence/content-types-utils';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import { useSpaceKey } from '@confluence/space-utils';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context';

import { messages } from '../messages';

import { ViewScriptButton } from './ViewScriptButton';

const marginTop = xcss({
	marginTop: 'space.100',
});
const marginBottom = xcss({
	marginBottom: 'space.100',
});

const loomIconStyles = xcss({
	width: '20px',
	height: '20px',
	marginLeft: 'space.negative.025',
	marginRight: 'space.075',
});

const HorizontalDivider = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	height: '1px',
	width: '135px',
});

const contentType = 'page';

export type LoadEditor = ({ args }: { [key: string]: string }) => void;

export type UseLoomScriptRecordingProps = {
	contentId: string;
	flags: FlagsStateContainer;
};

export const ReadTimeDropdownLoomScriptExtension = ({
	contentId,
	flags,
}: UseLoomScriptRecordingProps) => {
	const { loadEditor } = useEditPageLoadingActions();
	const loomInsertUrlActions = useLoomInsertUrlActions();
	const spaceKey = useSpaceKey() || '';
	const redirectUrl =
		getUrlForContentType({
			contentType,
			spaceKey,
			contentId,
			redirectToEditor: true,
		}) || '';

	const saveLoomUrlAndLoadEditor = useCallback(
		(video: LoomVideo) => {
			loomInsertUrlActions.updateFullPageEditorLoomUrl(video);

			loadEditor &&
				loadEditor({
					contentId,
					spaceKey,
					redirectUrl,
					contentType,
				});
		},
		[loomInsertUrlActions, contentId, spaceKey, loadEditor, redirectUrl],
	);

	const openLoomRecorderParams = useMemo(
		() => ({
			onInsert: (_: unknown, video: LoomVideo) => {
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		}),
		[saveLoomUrlAndLoadEditor],
	);

	const { openLoomRecorder, isOpeningRecorder } = useLoomRecorderEntryPoint({
		entryPointLocation: 'loomAIScriptReadMenuDropdown',
		flags,
		resetInsertFunctionOnUnmount: false,
	});

	const onRecordVideoClick = useCallback(() => {
		void openLoomRecorder(openLoomRecorderParams);
	}, [openLoomRecorder, openLoomRecorderParams]);
	return (
		<>
			<Box key="loomAIScriptSectionLabel" xcss={marginBottom}>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text.subtlest" size="small">
						<FormattedMessage {...messages.readTimeImprovementLabel} />
					</Text>
					<Box xcss={HorizontalDivider} />
				</Inline>
			</Box>

			<Box key="createSummaryLoomVideo" xcss={[marginTop, marginBottom]}>
				<Inline alignBlock="center" spread="space-between">
					<Inline alignBlock="center">
						<Flex alignItems="center" justifyContent="center" xcss={loomIconStyles}>
							<LoomIcon appearance="brand" size="small" />
						</Flex>
						<Box xcss={[marginTop, marginBottom]}>
							<Text weight="bold" size="medium">
								<FormattedMessage {...messages.readTimeLoomAIScriptTitle} />
							</Text>
						</Box>
					</Inline>
				</Inline>
			</Box>

			<Box key="loomAIScriptCTA" xcss={marginTop}>
				<Flex alignItems="center" justifyContent="start" gap="space.100">
					<Button iconBefore={VideoIcon} isLoading={isOpeningRecorder} onClick={onRecordVideoClick}>
						<FormattedMessage {...messages.recordVideoButton} />
					</Button>
					<ViewScriptButton />
				</Flex>
			</Box>
		</>
	);
};
